'use client';

import { useCopyToClipboard } from '@web/hooks';
import { InlineStyles } from '@web/types';

import { CheckIcon, LinkIcon } from './icons';
import { Tooltip } from './Tooltip';

type CopyLinkIconProps = {
  value?: string;
  ariaLabel?: string;
  size?: number;
  showTooltip?: boolean;
  iconStyles?: InlineStyles;
};

/**
 * * Copy-to-clipboard icon button with tooltip feedback.
 *
 * * Uses the copy hook for clipboard behavior and toggles a success state
 * * briefly to confirm the action.
 */
export function CopyLinkIcon({
  value,
  ariaLabel = 'Copy link',
  size = 18,
  showTooltip = true,
  iconStyles = {},
}: CopyLinkIconProps) {
  const { hasCopied, copy } = useCopyToClipboard({ value, timeout: 2000 });

  const tooltipTitle = hasCopied ? 'Copied!' : 'Copy link';
  const iconNode = hasCopied ? (
    <CheckIcon size={size} styles={iconStyles} />
  ) : (
    <LinkIcon
      size={size}
      showTooltip={false}
      tooltipTitle={tooltipTitle}
      styles={iconStyles}
    />
  );

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => {
        void copy();
      }}
      style={{
        alignItems: 'center',
        background: 'transparent',
        border: '0',
        cursor: 'pointer',
        display: 'inline-flex',
        padding: '0',
      }}
    >
      {showTooltip ? (
        <Tooltip title={tooltipTitle}>{iconNode}</Tooltip>
      ) : (
        iconNode
      )}
    </button>
  );
}
